/** @prettier */
//-----------------------------
//	グラフ表示コンポーネント
//-----------------------------

import {useTranslation} from 'react-i18next';
import {GraphDataRange, GraphTargetType} from '../../../Type/TypeDef';
import PolygonalLineGraph from '../../PolygonalLineGraph';

/**
 * SLTrendグラフ表示コンポーネント
 * @param props props
 * - dateRange データ取得範囲
 * - activeComparison 比較状態判定
 * - fileNameDate CSVファイル名の日付
 * - graphTitle グラフタイトル
 * - graphTarget グラフ描画対象
 * - xAxisLabel X軸に表示するラベル
 * - comparisonXAxisLabel 週比較の比較データを表示するグラフのX軸に表示するラベル
 * - trendGraphIndoorTemp 折れ線グラフデータ 室内温度
 * - trendGraphOutdoorTemp 折れ線グラフデータ 室外温度
 * - trendGraphPresetTemp 折れ線グラフデータ 設定温度
 * - comparisonTrendGraphIndoorTemp 折れ線グラフデータ 室内温度（比較日）
 * - comparisonTrendGraphOutdoorTemp 折れ線グラフデータ 室外温度（比較日）
 * - comparisonTrendGraphPresetTemp 折れ線グラフデータ 設定温度（比較日）
 * @returns JSX
 */
export const SLLineGraphDisplay = (props: {
  dateRange: GraphDataRange;
  activeComparison: boolean;
  fileNameDate: string;
  graphTitle: string;
  graphTarget: GraphTargetType;
  xAxisLabel: string[];
  comparisonXAxisLabel: string[];
  trendGraphIndoorTemp: ApexAxisChartSeries['0']['data'];
  trendGraphOutdoorTemp: ApexAxisChartSeries['0']['data'];
  trendGraphPresetTemp: ApexAxisChartSeries['0']['data'];
  comparisonTrendGraphIndoorTemp: ApexAxisChartSeries['0']['data'];
  comparisonTrendGraphOutdoorTemp: ApexAxisChartSeries['0']['data'];
  comparisonTrendGraphPresetTemp: ApexAxisChartSeries['0']['data'];
}) => {
  const trans = useTranslation().t; //翻訳関数

  //prettier-ignore
  /* グラフ表示用データ */
  const graphData = [
    {name: `${trans('GraphDisplay.slLineGraphDisplay.graphItem.indoorTemp')}`, data: props.trendGraphIndoorTemp},
    {name: `${trans('GraphDisplay.slLineGraphDisplay.graphItem.outdoorTemp')}`, data: props.trendGraphOutdoorTemp},
    {name: `${trans('GraphDisplay.slLineGraphDisplay.graphItem.presetTemp')}`, data: props.trendGraphPresetTemp}
  ];

  //prettier-ignore
  /* 比較グラフ表示用データ */
  const comparisonGraphData = [
    {name: `${trans('GraphDisplay.slLineGraphDisplay.graphItem.comparisonIndoorTemp')}`, data: props.comparisonTrendGraphIndoorTemp},
    {name: `${trans('GraphDisplay.slLineGraphDisplay.graphItem.comparisonOutdoorTemp')}`, data: props.comparisonTrendGraphOutdoorTemp},
    {name: `${trans('GraphDisplay.slLineGraphDisplay.graphItem.comparisonPresetTemp')}`, data: props.comparisonTrendGraphPresetTemp}
  ];

  /* グラフ凡例表示 */
  const graphLegend = [
    `${trans('GraphDisplay.slLineGraphDisplay.graphItem.indoorTemp')}`,
    `${trans('GraphDisplay.slLineGraphDisplay.graphItem.outdoorTemp')}`,
    `${trans('GraphDisplay.slLineGraphDisplay.graphItem.presetTemp')}`,
  ];

  //表示範囲が1日かつ比較状態の場合、グラフ表示用データに比較データを追加
  if (props.dateRange === 'day' && props.activeComparison) {
    graphData.push(...comparisonGraphData);
  }
  return (
    <PolygonalLineGraph
      xLabel={props.xAxisLabel}
      comparisonXLabel={props.comparisonXAxisLabel}
      yTitle={`${trans('GraphDisplay.slLineGraphDisplay.yAxisTitle')}`}
      csvFileNameDate={props.fileNameDate} //csvファイル名の日付(グラフタイトルとは表示形式が異なる)
      csvFileNameTarget={props.graphTitle} //グラフタイトルとcsvファイル名
      graph={graphData}
      comparisonGraph={comparisonGraphData}
      graphLegend={graphLegend}
      graphUnits={['temp', 'temp', 'temp']}
      activeComparison={props.activeComparison}
      graphTarget={props.graphTarget}
      dateRange={props.dateRange}
    />
  );
};

/**
 * 室内機Trendグラフ表示コンポーネント
 * @param props props
 * - graphType グラフの種類
 * - dateRange データ取得範囲
 * - activeComparison 比較状態判定
 * - csvFileNameDate  CSVファイル名の日付
 * - csvFileNameTarget グラフタイトルとcsvファイル名
 * - graphTarget グラフ描画対象
 * - xAxisLabel X軸に表示するラベル
 * - comparisonXAxisLabel 週比較の比較データを表示するグラフのX軸に表示するラベル
 * - trendGraphInsideTemp 折れ線グラフデータ 庫内温度
 * - trendGraphPresetTemp 折れ線グラフデータ 設定温度
 * - comparisonTrendGraphInsideTemp 折れ線グラフデータ 庫内温度（比較日）
 * - comparisonTrendGraphPresetTemp 折れ線グラフデータ 設定温度（比較日）
 * @returns JSX
 */
export const IndoorLineGraphDisplay = (props: {
  dateRange: GraphDataRange;
  activeComparison: boolean;
  csvFileNameDate: string;
  csvFileNameTarget: string;
  graphTarget: GraphTargetType;
  xAxisLabel: string[];
  comparisonXAxisLabel: string[];
  trendGraphInsideTemp: ApexAxisChartSeries['0']['data'];
  trendGraphPresetTemp: ApexAxisChartSeries['0']['data'];
  comparisonTrendGraphInsideTemp: ApexAxisChartSeries['0']['data'];
  comparisonTrendGraphPresetTemp: ApexAxisChartSeries['0']['data'];
}) => {
  const trans = useTranslation().t; //翻訳関数

  //prettier-ignore
  /** グラフ表示用データ */
  const graphData = [
    {name: `${trans('GraphDisplay.indoorLineGraphDisplay.graphItem.insideTemp')}`, data: props.trendGraphInsideTemp},
    {name: `${trans('GraphDisplay.indoorLineGraphDisplay.graphItem.presetTemp')}`, data: props.trendGraphPresetTemp},
  ];
  //prettier-ignore
  /** 比較グラフ表示用データ */
  const comparisonGraphData = [
    {name: `${trans('GraphDisplay.indoorLineGraphDisplay.graphItem.comparisonInsideTemp')}`, data: props.comparisonTrendGraphInsideTemp},
    {name: `${trans('GraphDisplay.indoorLineGraphDisplay.graphItem.comparisonPresetTemp')}`, data: props.comparisonTrendGraphPresetTemp}
  ];

  /** グラフ凡例表示 */
  const graphLegend = [
    `${trans('GraphDisplay.indoorLineGraphDisplay.graphItem.insideTemp')}`,
    `${trans('GraphDisplay.indoorLineGraphDisplay.graphItem.presetTemp')}`,
  ];

  //表示範囲が１日かつ比較状態の場合、グラフ表示用データに比較データを追加
  if (props.dateRange === 'day' && props.activeComparison) {
    graphData.push(...comparisonGraphData);
  }

  return (
    <PolygonalLineGraph
      xLabel={props.xAxisLabel}
      comparisonXLabel={props.comparisonXAxisLabel}
      yTitle={`${trans('GraphDisplay.indoorLineGraphDisplay.yAxisTitle')}`}
      csvFileNameDate={props.csvFileNameDate} //csvファイル名の日付(グラフタイトルとは表示形式が異なる)
      csvFileNameTarget={props.csvFileNameTarget} //グラフタイトルとcsvファイル名
      graph={graphData}
      comparisonGraph={comparisonGraphData}
      graphLegend={graphLegend}
      graphUnits={['temp', 'temp']}
      activeComparison={props.activeComparison}
      graphTarget={props.graphTarget}
      dateRange={props.dateRange}
    />
  );
};
